import { useEffect, useRef, useState } from 'react';
import type { CadDocument } from '../model/document';
import type { CadEntity } from '../model/entity';
import { entityBox } from '../model/entity';
import type { DocumentDiff } from '../diff/documentDiff';
import { Viewport } from '../render/viewport';
import { drawEntity, DARK_THEME } from '../render/renderer';
import type { Box } from '../model/geom';

/**
 * 비교 도면 (작업지시서 P4-09)
 *
 * ── 세 가지 보기 ────────────────────────────────────────────────────────
 *
 *   · 겹쳐보기 — 철거는 붉게, 신설은 푸르게, 유지는 흐리게 한 장에.
 *     이게 기본이다. 대부분의 판단은 한 장에서 끝난다.
 *   · 슬라이더 — 가운데 손잡이를 끌면 왼쪽은 현행, 오른쪽은 변경안.
 *     "여기가 실제로 어떻게 보이나" 를 볼 때 쓴다.
 *   · 나란히 — 회의에서 두 안을 동시에 가리킬 때.
 *
 * ★ 색만으로 구분하지 않는다. 겹쳐보기에서 철거는 **파선**, 신설은 실선이다.
 *   현장 출력물은 흑백인 경우가 많다.
 */

type Mode = 'overlay' | 'slider' | 'side';

const COLOR = {
  removed: '#ff6b6b',
  added: '#4dd4ac',
  moved: '#ffd166',
  modified: '#c9a0ff',
  keep: '#6b6b80',
};

export function CompareCanvas({
  before,
  after,
  diff,
  beforeLabel,
  afterLabel,
}: {
  before: CadDocument;
  after: CadDocument;
  diff: DocumentDiff;
  beforeLabel: string;
  afterLabel: string;
}) {
  const [mode, setMode] = useState<Mode>('overlay');
  const [split, setSplit] = useState(50);

  return (
    <section className="cmp__pane">
      <div className="cmp__paneHead">
        <div className="seg" role="tablist">
          {(
            [
              ['overlay', '겹쳐보기'],
              ['slider', '슬라이더'],
              ['side', '나란히'],
            ] as const
          ).map(([m, label]) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              className="seg__btn"
              onClick={() => setMode(m)}
            >
              {label}
            </button>
          ))}
        </div>

        <Legend mode={mode} beforeLabel={beforeLabel} afterLabel={afterLabel} />
      </div>

      {mode === 'overlay' && <OverlayView before={before} after={after} diff={diff} />}

      {mode === 'slider' && (
        <SliderView
          before={before}
          after={after}
          split={split}
          onSplit={setSplit}
          beforeLabel={beforeLabel}
          afterLabel={afterLabel}
        />
      )}

      {mode === 'side' && (
        <div className="cmp-side">
          <figure>
            <figcaption>{beforeLabel}</figcaption>
            <StaticView doc={before} fitTo={unionBox(before, after)} />
          </figure>
          <figure>
            <figcaption>{afterLabel}</figcaption>
            <StaticView doc={after} fitTo={unionBox(before, after)} />
          </figure>
        </div>
      )}
    </section>
  );
}

function Legend({
  mode,
  beforeLabel,
  afterLabel,
}: {
  mode: Mode;
  beforeLabel: string;
  afterLabel: string;
}) {
  if (mode === 'overlay') {
    return (
      <ul className="cmp-legend">
        <li>
          <span className="cmp-legend__dash" style={{ borderColor: COLOR.removed }} aria-hidden="true" />
          철거 (파선)
        </li>
        <li>
          <span className="cmp-legend__line" style={{ background: COLOR.added }} aria-hidden="true" />
          신설
        </li>
        <li>
          <span className="cmp-legend__line" style={{ background: COLOR.moved }} aria-hidden="true" />
          이설
        </li>
        <li>
          <span className="cmp-legend__line" style={{ background: COLOR.modified }} aria-hidden="true" />
          변경
        </li>
      </ul>
    );
  }
  return (
    <p className="cmp-legend cmp-legend__text">
      왼쪽 {beforeLabel} · 오른쪽 {afterLabel}
    </p>
  );
}

/* -------------------------------------------------------------------------- *
 * 겹쳐보기
 * -------------------------------------------------------------------------- */

function OverlayView({
  before,
  after,
  diff,
}: {
  before: CadDocument;
  after: CadDocument;
  diff: DocumentDiff;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const box = unionBox(before, after);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const draw = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      if (w <= 0 || h <= 0) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const vp = new Viewport();
      vp.resize(w, h);
      vp.fit(box);

      ctx.fillStyle = '#16161f';
      ctx.fillRect(0, 0, w, h);

      // 1) 유지 — 배경처럼 흐리게. 변경점이 눈에 들어와야 한다.
      paint(ctx, vp, diff.unchanged, after, COLOR.keep, 1, false, 0.35);

      // 2) 철거 — 파선. 색이 안 보이는 출력에서도 구분된다.
      paint(ctx, vp, diff.removed, before, COLOR.removed, 1.6, true, 1);

      // 3) 이동/변경 — 이전 위치를 파선으로, 새 위치를 실선으로
      paint(ctx, vp, diff.moved.map((m) => m.before), before, COLOR.moved, 1.2, true, 0.7);
      paint(ctx, vp, diff.moved.map((m) => m.after), after, COLOR.moved, 1.8, false, 1);
      paint(ctx, vp, diff.modified.map((m) => m.after), after, COLOR.modified, 1.8, false, 1);

      // 4) 신설 — 가장 위에, 가장 진하게
      paint(ctx, vp, diff.added, after, COLOR.added, 1.8, false, 1);
    };

    draw();
    const ro = new ResizeObserver(draw);
    if (canvas.parentElement) ro.observe(canvas.parentElement);
    return () => ro.disconnect();
  }, [before, after, diff, box]);

  return (
    <div className="cmp-canvas">
      <canvas ref={ref} />
    </div>
  );
}

/**
 * 지정한 엔티티들만 한 가지 색으로 그린다.
 *
 * `renderScene` 을 쓰지 않는 이유: 그쪽은 레이어 색으로 칠하고 그리드도 그린다.
 * 비교 화면에서 정보는 **레이어 색이 아니라 변경 종류**(철거/신설/이설)다.
 */
function paint(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  entities: CadEntity[],
  doc: CadDocument,
  color: string,
  width: number,
  dashed: boolean,
  alpha: number
): void {
  if (entities.length === 0) return;

  const layers = new Map(doc.layers.map((l) => [l.code, l]));
  const blocks = new Map(doc.blocks.map((b) => [b.code, b]));

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dashed ? [6, 4] : []);

  for (const e of entities) {
    // 편집기에서 숨긴 레이어는 비교에서도 숨긴다
    if (layers.get(e.layer)?.visible === false) continue;
    drawEntity(ctx, e, vp, blocks, DARK_THEME, doc.displayUnit);
  }

  ctx.restore();
}

/* -------------------------------------------------------------------------- *
 * 슬라이더
 * -------------------------------------------------------------------------- */

function SliderView({
  before,
  after,
  split,
  onSplit,
  beforeLabel,
  afterLabel,
}: {
  before: CadDocument;
  after: CadDocument;
  split: number;
  onSplit: (v: number) => void;
  beforeLabel: string;
  afterLabel: string;
}) {
  const box = unionBox(before, after);

  return (
    <div className="cmp-slider">
      <div className="cmp-slider__stack">
        <StaticView doc={before} fitTo={box} />
        <div className="cmp-slider__clip" style={{ width: `${split}%` }}>
          {/* 같은 화면 크기·같은 fit 이어야 두 도면이 정확히 겹친다 */}
          <StaticView doc={after} fitTo={box} matchWidthPercent={split} />
        </div>
        <div className="cmp-slider__handle" style={{ left: `${split}%` }} aria-hidden="true" />
      </div>

      <label className="cmp-slider__control">
        <span className="sr-only">비교 위치</span>
        <input
          type="range"
          min={0}
          max={100}
          value={split}
          onChange={(e) => onSplit(Number(e.target.value))}
          aria-label={`${beforeLabel} 과 ${afterLabel} 비교 위치`}
        />
      </label>
    </div>
  );
}

/* -------------------------------------------------------------------------- *
 * 단일 도면
 * -------------------------------------------------------------------------- */

function StaticView({
  doc,
  fitTo,
  matchWidthPercent,
}: {
  doc: CadDocument;
  fitTo: Box;
  /** 슬라이더에서 잘린 폭 안에 그리되, 화면 좌표는 전체 폭 기준으로 맞춘다 */
  matchWidthPercent?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const draw = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      const clipW = parent.clientWidth;
      const h = parent.clientHeight;
      if (clipW <= 0 || h <= 0) return;

      // 잘린 상태에서도 도면은 "전체 폭 기준" 으로 배치해야 두 장이 겹친다
      const fullW =
        matchWidthPercent !== undefined && matchWidthPercent > 0
          ? (clipW * 100) / matchWidthPercent
          : clipW;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(clipW * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${clipW}px`;
      canvas.style.height = `${h}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const vp = new Viewport();
      vp.resize(fullW, h);
      vp.fit(fitTo);

      ctx.fillStyle = '#16161f';
      ctx.fillRect(0, 0, clipW, h);

      // 단일 보기는 원래 레이어 색을 그대로 쓴다 — 실제 도면이 어떻게 보이는지가 목적이다
      const layers = new Map(doc.layers.map((l) => [l.code, l]));
      const blocks = new Map(doc.blocks.map((b) => [b.code, b]));
      const ordered = [...doc.entities].sort(
        (x, y) => (layers.get(x.layer)?.sortOrder ?? 999) - (layers.get(y.layer)?.sortOrder ?? 999)
      );

      for (const e of ordered) {
        const layer = layers.get(e.layer);
        if (layer && !layer.visible) continue;
        ctx.strokeStyle = e.color ?? layer?.color ?? '#cccccc';
        ctx.fillStyle = ctx.strokeStyle;
        ctx.lineWidth = 1.2;
        ctx.setLineDash(layer?.lineType === 'dashed' ? [6, 4] : []);
        drawEntity(ctx, e, vp, blocks, DARK_THEME, doc.displayUnit);
      }
    };

    draw();
    const ro = new ResizeObserver(draw);
    if (canvas.parentElement) ro.observe(canvas.parentElement);
    return () => ro.disconnect();
  }, [doc, fitTo, matchWidthPercent]);

  return <canvas ref={ref} className="cmp-canvas__el" />;
}

/* -------------------------------------------------------------------------- */

/**
 * 두 도면을 아우르는 범위.
 *
 * ★ 각자 fit 하면 안 된다. 축척이 달라져 "같은 자리" 가 다른 자리에 그려지고,
 *   슬라이더로 훑을 때 벽이 어긋나 보인다.
 */
export function unionBox(a: CadDocument, b: CadDocument): Box {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const doc of [a, b]) {
    const blocks = new Map(doc.blocks.map((x) => [x.code, x]));
    for (const e of doc.entities) {
      const box = entityBox(e, blocks);
      minX = Math.min(minX, box.minX);
      minY = Math.min(minY, box.minY);
      maxX = Math.max(maxX, box.maxX);
      maxY = Math.max(maxY, box.maxY);
    }
  }

  // 둘 다 비었으면 임의의 1m 사각으로 — fit 이 NaN 을 만들면 화면이 하얗게 된다
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 1_000_000, maxY: 1_000_000 };
  return { minX, minY, maxX, maxY };
}
